import { useRef } from 'react';

import clsx from 'clsx';

import {
  FeaturedCollectionsTrackPromotionEvent,
  createFeaturedCollections,
} from '@/common/analytics/gtm/Homepage/featuredCollectionsAnalytics';
import { trackGA4SelectPromotion } from '@/common/analytics/gtm/shared/trackPromotionClick';
import { ArtworkResolutions, imageCoreToUrl } from '@/common/components/ArtworkPicture/artworkUri';
import Avatar from '@/common/components/Avatar/Avatar';
import { ResolutionsSrcSet } from '@/common/constants/displateRoutes';
import { xLarge3 } from '@/common/constants/media';
import { UseSlideImpression } from '@/common/impressions/useSliderImpression';
import { RouteToArtistPage, RouteToCollectionPage } from '@/common/routes/ExternalRoutes';
import {
  ArtworkOrientation,
  HomepageFeaturedCollection,
  HomepageFeaturedCollectionsImage,
} from 'generatedBfw/static';

import styles from './collectionTile.module.scss';

interface Props {
  collection: HomepageFeaturedCollection;
  className?: string;
  position: number;
  useSlideImpression: UseSlideImpression<FeaturedCollectionsTrackPromotionEvent>;
}

const createSingleImage = (image: HomepageFeaturedCollectionsImage) => {
  const coreUrl = `${image.coreUrl}.jpg`;

  return (
    <div className={styles.imageWrapper} key={coreUrl}>
      <picture>
        <source
          media={`(max-width: ${xLarge3}px)`}
          srcSet={ResolutionsSrcSet({
            x1: imageCoreToUrl(
              coreUrl,
              ArtworkResolutions.rect.rect190x135,
              ArtworkOrientation.VERTICAL,
            ),
            x2: imageCoreToUrl(
              coreUrl,
              ArtworkResolutions.rect.rect380x270,
              ArtworkOrientation.VERTICAL,
            ),
          })}
        />
        <source
          media={`(min-width: ${xLarge3 + 1}px)`}
          srcSet={ResolutionsSrcSet({
            x1: imageCoreToUrl(
              coreUrl,
              ArtworkResolutions.rect.rect255x182,
              ArtworkOrientation.VERTICAL,
            ),
            x2: imageCoreToUrl(
              coreUrl,
              ArtworkResolutions.rect.rect640x460,
              ArtworkOrientation.VERTICAL,
            ),
          })}
        />
        <img
          alt={image.title}
          className={styles.image}
          data-testid="featured-collections-image"
          height={190}
          loading="lazy"
          src={imageCoreToUrl(
            coreUrl,
            ArtworkResolutions.rect.rect190x135,
            ArtworkOrientation.VERTICAL,
          )}
          width={135}
        />
      </picture>
    </div>
  );
};

const CollectionTile = ({
  collection: collectionProp,
  className,
  useSlideImpression,
  position,
}: Props) => {
  const ref = useRef(null);
  const { author, collection, images } = collectionProp;
  const eventMessage = createFeaturedCollections(collectionProp, position);
  useSlideImpression(ref, eventMessage);

  const handleClick = () => {
    trackGA4SelectPromotion(eventMessage);
  };

  return (
    <div
      className={clsx(styles.container, className)}
      data-testid="featured-collections-tile"
      ref={ref}
    >
      <div className={styles.images}>{images.map(createSingleImage)}</div>
      <div className={styles.info}>
        <Avatar
          author={author}
          className={styles.avatar}
          imageClassName={styles.avatarImage}
          size="small"
        />
        <div className={styles.text}>
          <a
            className={styles.name}
            data-testid="featured-collections-title"
            href={
              RouteToCollectionPage({ artistNick: author.nick, collectionSlug: collection.slug })
                .route
            }
            onClick={handleClick}
          >
            <h3>{collection.title}</h3>
          </a>
          <a
            className={styles.artist}
            data-testid="featured-collections-author"
            href={RouteToArtistPage({ licensed: author.isLicensed, nick: author.nick }).route}
            onClick={handleClick}
          >
            by {author.name}
          </a>
        </div>
      </div>
    </div>
  );
};

export default CollectionTile;
